import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getRoles,
  updateRole,
  getQuestions,
  createQuestion,
  deleteQuestion,
} from '../api/client.js';

const ROLE_COLOUR_PRESETS = [
  { label: 'Expressway blue', value: '#3B82F6' },
  { label: 'LTC green', value: '#22C55E' },
  { label: 'LTABLE orange', value: '#F97316' },
  { label: 'LTPT purple', value: '#A855F7' },
  { label: 'Personal red', value: '#EF4444' },
];

export default function RolesAndQuestions() {
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState(null);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const refreshRoles = useCallback(async () => {
    setRolesError(null);
    setRolesLoading(true);
    try {
      const list = await getRoles();
      const sorted = [...list].sort((a, b) => a.priority_order - b.priority_order);
      setRoles(sorted);
      if (sorted.length && selectedId == null) setSelectedId(sorted[0].id);
    } catch (err) {
      setRolesError(err.message || String(err));
    } finally {
      setRolesLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    refreshRoles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedId) || null,
    [roles, selectedId]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] gap-6">
      <aside className="bg-white rounded-2xl shadow p-4">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Roles
        </h2>
        {rolesLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {rolesError && (
          <p className="text-sm text-rose-600">Failed to load roles: {rolesError}</p>
        )}
        {!rolesLoading && !rolesError && (
          <ul className="space-y-1">
            {roles.map((r) => {
              const isSelected = r.id === selectedId;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-3 transition ${
                      isSelected
                        ? 'bg-slate-900 text-white'
                        : 'hover:bg-slate-100 text-slate-700'
                    }`}
                  >
                    <span
                      className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: r.colour }}
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate">{r.short_name}</span>
                    <span
                      className={`text-xs ${
                        isSelected ? 'text-slate-300' : 'text-slate-400'
                      }`}
                    >
                      #{r.priority_order}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      <section className="space-y-6">
        {selectedRole ? (
          <>
            <RoleEditor
              role={selectedRole}
              onSaved={(updated) => {
                setRoles((prev) => {
                  const next = prev.map((r) => (r.id === updated.id ? updated : r));
                  next.sort((a, b) => a.priority_order - b.priority_order);
                  return next;
                });
              }}
            />
            <QuestionsPanel role={selectedRole} />
          </>
        ) : (
          !rolesLoading && (
            <p className="text-sm text-slate-500">Select a role to manage its questions.</p>
          )
        )}
      </section>
    </div>
  );
}

function RoleEditor({ role, onSaved }) {
  const [name, setName] = useState(role.name);
  const [shortName, setShortName] = useState(role.short_name);
  const [colour, setColour] = useState(role.colour);
  const [priority, setPriority] = useState(role.priority_order);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setName(role.name);
    setShortName(role.short_name);
    setColour(role.colour);
    setPriority(role.priority_order);
    setError(null);
    setSavedAt(null);
  }, [role.id, role.name, role.short_name, role.colour, role.priority_order]);

  const dirty =
    name !== role.name ||
    shortName !== role.short_name ||
    colour !== role.colour ||
    Number(priority) !== role.priority_order;

  async function onSave(e) {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateRole(role.id, {
        name: name.trim(),
        short_name: shortName.trim(),
        colour: colour.trim(),
        priority_order: Number(priority),
      });
      if (updated) {
        onSaved(updated);
        setSavedAt(new Date());
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={onSave} className="bg-white rounded-2xl shadow p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Role details</h2>
          <p className="text-xs text-slate-500">
            Name, short name, colour, and priority order. Colour drives badges across the app.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full"
          style={{ backgroundColor: `${colour}1A`, color: colour }}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: colour }}
          />
          {shortName || '—'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            required
          />
        </Field>
        <Field label="Short name">
          <input
            type="text"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            className="input"
            required
          />
        </Field>
        <Field label="Colour (hex)">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={colour}
              onChange={(e) => setColour(e.target.value)}
              className="h-10 w-12 rounded-md border border-slate-200 cursor-pointer"
              aria-label="Pick colour"
            />
            <input
              type="text"
              value={colour}
              onChange={(e) => setColour(e.target.value)}
              className="input flex-1 font-mono uppercase"
              pattern="^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$"
              required
            />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {ROLE_COLOUR_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setColour(p.value)}
                title={p.label}
                aria-label={p.label}
                className={`w-6 h-6 rounded-full border ${
                  colour.toLowerCase() === p.value.toLowerCase()
                    ? 'border-slate-900 ring-2 ring-slate-200'
                    : 'border-slate-200'
                }`}
                style={{ backgroundColor: p.value }}
              />
            ))}
          </div>
        </Field>
        <Field label="Priority order">
          <input
            type="number"
            min={1}
            max={99}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="input"
            required
          />
        </Field>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <div className="text-xs">
          {error && <span className="text-rose-600">Save failed: {error}</span>}
          {!error && savedAt && (
            <span className="text-emerald-600">
              Saved {savedAt.toLocaleTimeString()}.
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={!dirty || saving}
          className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

function QuestionsPanel({ role }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const list = await getQuestions(role.id);
      setQuestions(list);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [role.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function onAdd(e) {
    e.preventDefault();
    const trimmed = newText.trim();
    if (!trimmed || adding) return;
    setAdding(true);
    setError(null);
    try {
      const created = await createQuestion(role.id, trimmed);
      if (created) {
        setQuestions((prev) => [...prev, created]);
        setNewText('');
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setAdding(false);
    }
  }

  async function onDelete(id) {
    if (deletingId) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteQuestion(id);
      setQuestions((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Review questions</h2>
        <p className="text-xs text-slate-500">
          Asked during the weekly wizard for {role.short_name}. Add or remove freely.
        </p>
      </div>

      {error && (
        <p className="text-sm text-rose-600 bg-rose-50 rounded-md px-3 py-2">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Loading questions…</p>
      ) : questions.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No active questions. Add one below.</p>
      ) : (
        <ol className="space-y-2">
          {questions.map((q, idx) => (
            <li
              key={q.id}
              className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-slate-100 hover:border-slate-200 bg-slate-50/50"
            >
              <span className="text-xs font-mono text-slate-400 mt-0.5 w-5 text-right">
                {idx + 1}.
              </span>
              <p className="flex-1 text-sm text-slate-800">{q.question_text}</p>
              <button
                type="button"
                onClick={() => onDelete(q.id)}
                disabled={deletingId === q.id}
                className="text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
                aria-label={`Remove question ${idx + 1}`}
              >
                {deletingId === q.id ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ol>
      )}

      <form onSubmit={onAdd} className="flex items-center gap-2 pt-2 border-t">
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Add a new review question…"
          className="input flex-1"
          maxLength={500}
        />
        <button
          type="submit"
          disabled={!newText.trim() || adding}
          className="px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {adding ? 'Adding…' : 'Add'}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
